/**
 * Everything the details screen knows about whether the shipment can be quoted.
 *
 * Every error and warning message on the screen is computed here, along with
 * the single `isValid` that gates the get-quotes button. Keeping them together
 * is the point: the messages the user reads and the rule that blocks them have
 * to agree, and they only do if they're derived from the same place.
 */

import { isFreightClassRequired } from "@/Helpers/isFreightClassRequired";
import {
  totalHandlingUnitsForLineItems,
  totalWeightForLineItems,
} from "@/Helpers/lineItemCalculations";
import { locationsErrorMessageForBranches } from "@/Shared/Locations/Validators/locationsErrorMessageForBranches";
import { locationsErrorMessageForInsurance } from "@/Shared/Locations/Validators/locationsErrorMessageForInsurance";
import {
  locationsErrorMessagesForDeliveryLocation,
  warningMessagesForDeliveryLocation,
} from "@/Shared/Locations/Validators/locationsErrorMessagesForDeliveryLocation";
import {
  locationsErrorMessagesForPickupLocation,
  warningMessagesForPickupLocation,
} from "@/Shared/Locations/Validators/locationsErrorMessagesForPickupLocation";
import type { LineItemErrors } from "@/Shared/LineItems/Validators/lineItemsErrorMessages";
import {
  isProtectFromFreezingOutOfSeason,
  lineItemsErrorMessages,
} from "@/Shared/LineItems/Validators/lineItemsErrorMessages";
import { useLineItemsShipmentLimits } from "@/Shared/LineItems/Validators/useLineItemsShipmentLimits";
import { LineItemsTooManyLineItemsQuestionBubble } from "@/Shared/LineItems/Bubbles/LineItemsTooManyLineItemsQuestionBubble";
import type { UUID } from "@/Shared/types";
import type { SavedLocation } from "@freightsimple/generated-dashboard-openapi-client/models/SavedLocation";
import { bookShipmentIsPickupDateInThePast } from "../Helpers/bookShipmentIsPickupDateInThePast";
import type { BookShipmentShipmentType } from "@/Shared/Types/bookShipmentSliceTypes";
import { useBookShipmentWarningMessages } from "./useBookShipmentWarningMessages";

interface BookShipmentDetailsValidationInput {
  shipment: BookShipmentShipmentType;
  branches: Array<SavedLocation>;
  /** Line items whose NMFC lookup is currently in error. */
  nmfcErrors: Set<UUID>;
}

export function useBookShipmentDetailsValidation(
  input: BookShipmentDetailsValidationInput,
) {
  const { shipment, branches, nmfcErrors } = input;
  const {
    pickupDate,
    pickupLocation,
    deliveryLocation,
    lineItems,
    addInsuranceToShipment,
    insuranceAmount,
    stateOfGoods,
    branchId,
  } = shipment;

  const errorMessagesForPickup = locationsErrorMessagesForPickupLocation(
    pickupDate,
    pickupLocation,
  );
  const errorMessagesForDelivery = locationsErrorMessagesForDeliveryLocation(
    pickupLocation,
    deliveryLocation,
  );

  const warningMessagesForPickup =
    warningMessagesForPickupLocation(pickupLocation);
  const warningMessagesForDelivery = warningMessagesForDeliveryLocation(
    pickupLocation,
    deliveryLocation,
  );

  const totalWeightOfShipment = totalWeightForLineItems(lineItems);
  const totalHandlingUnitsOfShipment =
    totalHandlingUnitsForLineItems(lineItems);
  const shipmentLimits = useLineItemsShipmentLimits();

  // Shipment-wide: depends only on the pickup date, so it is the same for every
  // line item. Drives disabling Protect From Freezing in the dropdown.
  const protectFromFreezingOutOfSeason = isProtectFromFreezingOutOfSeason(
    pickupDate,
    shipmentLimits,
  );

  const freightClassRequired = isFreightClassRequired(
    pickupLocation,
    deliveryLocation,
  );

  const errorMessagesForLineItems = new Map<string, LineItemErrors>();
  lineItems.forEach(function (li) {
    errorMessagesForLineItems.set(
      li.lineItemId,
      lineItemsErrorMessages(
        pickupDate,
        li,
        totalWeightOfShipment,
        totalHandlingUnitsOfShipment,
        freightClassRequired,
        pickupLocation,
        deliveryLocation,
        shipmentLimits,
      ).errorMessages,
    );
  });

  const insuranceErrorMessage = locationsErrorMessageForInsurance(
    addInsuranceToShipment,
    insuranceAmount,
    pickupLocation,
    deliveryLocation,
  );

  const stateOfGoodsErrorMessages =
    stateOfGoods === undefined && addInsuranceToShipment
      ? "Please select a value"
      : undefined;

  const branchesErrorMessage = locationsErrorMessageForBranches(
    branches,
    branchId,
  );

  // The line-item cap is on the shipment, not on any one item, so it is the
  // one line-item error that lives under the list rather than on a card. The
  // form stops adding at the cap, so this only fires for a draft or requote
  // that arrives over it.
  const tooManyLineItems =
    lineItems.length > shipmentLimits.shipment.maxLineItems;

  function overallLineItemsError() {
    if (lineItems.length === 0) {
      return "Please add some contents to the shipment";
    }
    if (tooManyLineItems) {
      return (
        <>
          Too many line items{" "}
          <LineItemsTooManyLineItemsQuestionBubble
            maxLineItems={shipmentLimits.shipment.maxLineItems}
          />
        </>
      );
    }
    return undefined;
  }
  const overallLineItemsErrorMessage = overallLineItemsError();

  // Do not allow quotes to be run for pickups in the past
  // The control doesn't allow picking this, but you could modify a quote that was run a few days
  // ago and end up in this situation
  const pickupDateIsInThePast = bookShipmentIsPickupDateInThePast(pickupDate);

  function validateDate() {
    if (pickupDateIsInThePast) {
      return false;
    }

    // TODO: We need more sophisticated validation

    return true;
  }

  function validate() {
    // Check the date is valid
    if (!validateDate()) {
      return false;
    }

    if (lineItems.length === 0) {
      return false;
    }

    // Drafts and requotes can arrive with more items than the cap, so this is
    // checked here and not only where items are added.
    if (tooManyLineItems) {
      return false;
    }

    if (nmfcErrors.size > 0) {
      return false;
    }

    if (
      !lineItems.every(
        (li) =>
          lineItemsErrorMessages(
            pickupDate,
            li,
            totalWeightOfShipment,
            totalHandlingUnitsOfShipment,
            isFreightClassRequired(pickupLocation, deliveryLocation),
            pickupLocation,
            deliveryLocation,
            shipmentLimits,
          ).isValid,
      )
    ) {
      return false;
    }

    if (errorMessagesForPickup.address.postalCode) {
      return false;
    }

    if (errorMessagesForPickup.locationType) {
      return false;
    }

    if (errorMessagesForDelivery.address.postalCode) {
      return false;
    }

    if (errorMessagesForDelivery.locationType) {
      return false;
    }

    if (insuranceErrorMessage !== undefined) {
      return false;
    }

    if (stateOfGoodsErrorMessages !== undefined) {
      return false;
    }

    if (branchesErrorMessage !== undefined) {
      return false;
    }

    return true;
  }

  const { warnings } = useBookShipmentWarningMessages({
    pickupDate,
    lineItems,
    pickupLocation,
    deliveryLocation,
  });

  return {
    errorMessagesForPickup,
    errorMessagesForDelivery,
    warningMessagesForPickup,
    warningMessagesForDelivery,
    errorMessagesForLineItems,
    insuranceErrorMessage,
    stateOfGoodsErrorMessages,
    branchesErrorMessage,
    overallLineItemsErrorMessage,
    maxLineItems: shipmentLimits.shipment.maxLineItems,
    maxHandlingUnits: shipmentLimits.shipment.maxHandlingUnits,
    freightClassRequired,
    protectFromFreezingOutOfSeason,
    pickupDateIsInThePast,
    warnings,
    isValid: validate(),
  };
}
